import React, { Suspense, useEffect, useRef } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { CSpinner, useColorModes } from '@coreui/react'
import './scss/style.scss'
import ForgotPassword from './views/pages/forgotPassword/ForgotPassword'
import ResetPassword from './views/pages/resetPassword/ResetPassword'
import { trackAdminVisit } from './services/actions/adminActivity'
import { persistUserTimezone } from './services/timezone'

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))
// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'))
const Register = React.lazy(() => import('./views/pages/register/Register'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))

const App = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isColorModeSet, setColorMode } = useColorModes('coreui-free-react-admin-template-theme')
  const storedTheme = useSelector((state) => state.theme)
  const lastTrackedPathRef = useRef('')
  const lastTrackedPageKey = 'adminActivity:lastTrackedPage'
  const publicLocations = ['/', '/forgotpassword', '/resetpassword', '/register', '/login', '/404', '/500']
  useEffect(() => {
    if (publicLocations.includes(location.pathname) || location.pathname?.includes('/resetpassword')) {
      return
    }
    const token = JSON.parse(localStorage.getItem("token"));
    if (!token) {
      const returnPath = `${location.pathname}${location.search || ''}`
      navigate(`/?returnUrl=${encodeURIComponent(returnPath)}`)
      return
    }
    try {
      const userData = JSON.parse(localStorage.getItem('userData')) || {}
      const resolvedTimezone = userData?.timezone || localStorage.getItem('userTimezone')
      if (resolvedTimezone) {
        persistUserTimezone(resolvedTimezone)
      }
    } catch {
      // ignore localStorage parse errors
    }
  }, [location.pathname])

  useEffect(() => {
    if (publicLocations.includes(location.pathname) || location.pathname?.includes('/resetpassword')) {
      return
    }

    const token = JSON.parse(localStorage.getItem('token'))
    if (!token) {
      return
    }

    const pageUrl = `${location.pathname}${location.search || ''}`
    if (lastTrackedPathRef.current === pageUrl) {
      return
    }

    const lastTrackedPage = sessionStorage.getItem(lastTrackedPageKey)
    if (lastTrackedPage === pageUrl) {
      return
    }

    lastTrackedPathRef.current = pageUrl
    sessionStorage.setItem(lastTrackedPageKey, pageUrl)
    trackAdminVisit(pageUrl)
  }, [location.pathname, location.search])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.href.split('?')[1])
    const theme = urlParams.get('theme') && urlParams.get('theme').match(/^[A-Za-z0-9\s]+/)[0]
    if (theme) {
      setColorMode(theme)
    }

    if (isColorModeSet()) {
      return
    }

    setColorMode(storedTheme)
  }, [])

  return (
    <Suspense
      fallback={
        <div className="pt-3 text-center">
          <CSpinner color="primary" variant="grow" />
        </div>
      }
    >
      <Routes>
        <Route exact path="/" name="Login Page" element={<Login />} />
        <Route exact path="/login" name="Login Page" element={<Login />} />
        <Route exact path="/register" name="Register Page" element={<Register />} />
        <Route exact path="/404" name="Page 404" element={<Page404 />} />
        <Route exact path="/500" name="Page 500" element={<Page500 />} />
        <Route exact path="/forgotpassword" name="Forgot Password" element={<ForgotPassword />} />
        <Route exact path="/resetpassword/:resetToken" name="Reset Password" element={<ResetPassword />} />
        <Route path="*" name="Home" element={<DefaultLayout />} />
      </Routes>
    </Suspense>
  )
}

export default App
